import { apolloClient } from '~/apollo-client';
import { ExtractUtilityBillResponse } from '~/services/extract-utility-bill';
import {
  BillExtractionQualificationStatus,
  BillExtractionSecondaryAccountIdentifierType,
  Flex_CreateUtilityAccountBillExtractionMutation,
  Flex_CreateUtilityAccountBillExtractionMutationVariables,
} from '~/typings/graphql';
import { CREATE_UTILITY_ACCOUNT_BILL_EXTRACTION } from './save-ai-utility-bill-extraction.queries';

/** Matches EP qualification_status enum values. */
export enum SaveAIUtilityBillExtractionQualificationStatus {
  Qualified = 'qualified',
  NeedsReview = 'needs_review',
  Failed = 'failed',
}

/** Matches EP secondary_account_identifier_type enum values. */
export enum SaveAIUtilityBillExtractionSecondaryIdType {
  PodId = 'pod_id',
  ElectricChoiceId = 'electric_choice_id',
  ResidentialServiceNumber = 'residential_service_number',
  CustomerNumber = 'customer_number',
  PremisesNumber = 'premises_number',
}

export type SaveAIUtilityBillExtractionParams = {
  accountNumber?: string | null;
  annualUsageKwh?: number | null;
  extractionId: string;
  meterNumber?: string | null;
  qualificationStatus?: SaveAIUtilityBillExtractionQualificationStatus | null;
  secondaryId?: string | null;
  secondaryIdType?: SaveAIUtilityBillExtractionSecondaryIdType | null;
  serviceAddress?: string | null;
  serviceAddressStreetOne?: string | null;
  serviceAddressStreetTwo?: string | null;
  serviceAddressCity?: string | null;
  serviceAddressState?: string | null;
  serviceAddressZip?: string | null;
  utilityAccountId?: number | null;
  utilityAccountName?: string | null;
};

export type SaveAIUtilityBillExtractionResponse = {
  id: number;
};

/** Maps local EP-facing status -> GraphQL BillExtractionQualificationStatus. */
const toGraphqlQualificationStatus = (
  status: SaveAIUtilityBillExtractionQualificationStatus,
): BillExtractionQualificationStatus => {
  switch (status) {
    case SaveAIUtilityBillExtractionQualificationStatus.Qualified:
      return BillExtractionQualificationStatus.Qualified;
    case SaveAIUtilityBillExtractionQualificationStatus.NeedsReview:
      return BillExtractionQualificationStatus.NeedsReview;
    case SaveAIUtilityBillExtractionQualificationStatus.Failed:
      return BillExtractionQualificationStatus.Failed;
  }
};

/** Maps local EP-facing secondary ID type -> GraphQL BillExtractionSecondaryAccountIdentifierType. */
const toGraphqlSecondaryIdType = (
  secondaryIdType: SaveAIUtilityBillExtractionSecondaryIdType | null | undefined,
): BillExtractionSecondaryAccountIdentifierType | null | undefined => {
  if (secondaryIdType == null) {
    return secondaryIdType;
  }

  switch (secondaryIdType) {
    case SaveAIUtilityBillExtractionSecondaryIdType.PodId:
      return BillExtractionSecondaryAccountIdentifierType.PodId;
    case SaveAIUtilityBillExtractionSecondaryIdType.ElectricChoiceId:
      return BillExtractionSecondaryAccountIdentifierType.ElectricChoiceId;
    case SaveAIUtilityBillExtractionSecondaryIdType.ResidentialServiceNumber:
      return BillExtractionSecondaryAccountIdentifierType.ResidentialServiceNumber;
    case SaveAIUtilityBillExtractionSecondaryIdType.CustomerNumber:
      return BillExtractionSecondaryAccountIdentifierType.CustomerNumber;
    case SaveAIUtilityBillExtractionSecondaryIdType.PremisesNumber:
      return BillExtractionSecondaryAccountIdentifierType.PremisesNumber;
  }
};

/** Maps AI-validation-api qualification status -> EP enum. */
const toEpQualificationStatus = (
  status: ExtractUtilityBillResponse['qualificationStatus'],
): SaveAIUtilityBillExtractionQualificationStatus | null => {
  switch (status) {
    case 'Qualified':
      return SaveAIUtilityBillExtractionQualificationStatus.Qualified;
    case 'Needs Review':
      return SaveAIUtilityBillExtractionQualificationStatus.NeedsReview;
    case 'Failed':
      return SaveAIUtilityBillExtractionQualificationStatus.Failed;
    default:
      return null;
  }
};

const buildServiceAddress = (
  extractionData: ExtractUtilityBillResponse['extractionData'] | null | undefined,
): string | null | undefined => {
  if (!extractionData) {
    return null;
  }

  if (extractionData.serviceAddress) {
    return extractionData.serviceAddress;
  }

  const isPresent = (part: string | null | undefined): part is string =>
    Boolean(part && part.trim());

  const stateZip = [extractionData.state, extractionData.zipCode].filter(isPresent).join(' ');

  const parts = [
    extractionData.address1,
    extractionData.address2,
    extractionData.city,
    stateZip,
  ].filter(isPresent);

  return parts.length > 0 ? parts.join(', ') : null;
};

const trimPresent = (value: string | null | undefined): string | null => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  return null;
};

/**
 * Secondary ID priority: POD → Electric Choice ID → RSN → customerNumber → premises.
 */
const resolveSecondaryId = (
  extractionData: ExtractUtilityBillResponse['extractionData'] | null | undefined,
): {
  secondaryId: string | null;
  secondaryIdType: SaveAIUtilityBillExtractionSecondaryIdType | null;
} => {
  const candidates: Array<{
    type: SaveAIUtilityBillExtractionSecondaryIdType;
    value: string | null;
  }> = [
    {
      type: SaveAIUtilityBillExtractionSecondaryIdType.PodId,
      value: trimPresent(extractionData?.podNumber),
    },
    {
      type: SaveAIUtilityBillExtractionSecondaryIdType.ElectricChoiceId,
      value: trimPresent(extractionData?.electricChoiceId),
    },
    {
      type: SaveAIUtilityBillExtractionSecondaryIdType.ResidentialServiceNumber,
      value: trimPresent(extractionData?.residentialServiceNumber),
    },
    {
      type: SaveAIUtilityBillExtractionSecondaryIdType.CustomerNumber,
      value: trimPresent(extractionData?.customerNumber),
    },
    {
      type: SaveAIUtilityBillExtractionSecondaryIdType.PremisesNumber,
      value: trimPresent(extractionData?.premisesNumber),
    },
  ];

  const match = candidates.find(candidate => candidate.value != null);
  return {
    secondaryId: match?.value ?? null,
    secondaryIdType: match?.type ?? null,
  };
};

export const fetchAIUtilityBillExtractionParamsToSave = ({
  response,
  utilityAccountId,
}: {
  response: ExtractUtilityBillResponse;
  utilityAccountId?: number;
}): SaveAIUtilityBillExtractionParams | null => {
  if (!response.id) {
    return null;
  }

  const { extractionData } = response;
  const { secondaryId, secondaryIdType } = resolveSecondaryId(extractionData);

  return {
    accountNumber: extractionData?.accountNumber ?? null,
    annualUsageKwh: extractionData?.annualUsageKwh ?? null,
    extractionId: response.id,
    meterNumber: extractionData?.meterNumber ?? null,
    qualificationStatus: toEpQualificationStatus(response.qualificationStatus),
    secondaryId,
    secondaryIdType,
    serviceAddress: buildServiceAddress(extractionData) ?? null,
    serviceAddressStreetOne: trimPresent(extractionData?.address1),
    serviceAddressStreetTwo: trimPresent(extractionData?.address2),
    serviceAddressCity: trimPresent(extractionData?.city),
    serviceAddressState: trimPresent(extractionData?.state),
    serviceAddressZip: trimPresent(extractionData?.zipCode),
    utilityAccountId: utilityAccountId ?? null,
    utilityAccountName: extractionData?.customerName ?? null,
  };
};

export const saveAIUtilityBillExtraction = async (
  params: SaveAIUtilityBillExtractionParams,
): Promise<SaveAIUtilityBillExtractionResponse | undefined> => {
  const { extractionId, qualificationStatus, utilityAccountId } = params;

  if (utilityAccountId == null || !extractionId || qualificationStatus == null) {
    return undefined;
  }

  const response = await apolloClient.mutate<
    Flex_CreateUtilityAccountBillExtractionMutation,
    Flex_CreateUtilityAccountBillExtractionMutationVariables
  >({
    mutation: CREATE_UTILITY_ACCOUNT_BILL_EXTRACTION,
    variables: {
      input: {
        utilityAccountId,
        extractionId,
        qualificationStatus: toGraphqlQualificationStatus(qualificationStatus),
        accountNumber: params.accountNumber,
        utilityAccountName: params.utilityAccountName,
        annualUsageKwh: params.annualUsageKwh,
        meterNumber: params.meterNumber,
        secondaryId: params.secondaryId,
        secondaryIdType: toGraphqlSecondaryIdType(params.secondaryIdType),
        serviceAddress: params.serviceAddress,
        serviceAddressStreetOne: params.serviceAddressStreetOne,
        serviceAddressStreetTwo: params.serviceAddressStreetTwo,
        serviceAddressCity: params.serviceAddressCity,
        serviceAddressState: params.serviceAddressState,
        serviceAddressZip: params.serviceAddressZip,
      },
    },
  });

  const id = response.data?.createUtilityAccountBillExtraction.id;
  return id != null ? { id } : undefined;
};
